'use client';

import { useState } from 'react';
import { X, Globe, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface AddMCPServerModalProps {
  agentId: string;
  onClose: () => void;
  onServerAdded: () => void;
  source?: 'chat-admin' | 'widget-admin';
}

export function AddMCPServerModal({ agentId, onClose, onServerAdded, source = 'widget-admin' }: AddMCPServerModalProps) {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    authType: 'none' as 'none' | 'bearer' | 'custom' | 'oauth2',
    apiKey: '',
    customHeaders: '',
    oauthClientId: '',
    oauthClientSecret: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Validate custom headers if provided
      if (formData.authType === 'custom' && formData.customHeaders) {
        try {
          JSON.parse(formData.customHeaders);
        } catch (e) {
          showToast('Invalid JSON format for custom headers', 'error');
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch(`/api/agents/${agentId}/mcp-servers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          url: formData.url,
          authType: formData.authType,
          authConfig: formData.authType === 'none' ? undefined : {
            token: formData.authType === 'bearer' ? formData.apiKey : undefined,
            headers: formData.authType === 'custom' && formData.customHeaders ? 
              JSON.parse(formData.customHeaders) : undefined,
            clientId: formData.authType === 'oauth2' && formData.oauthClientId ? 
              formData.oauthClientId : undefined,
            clientSecret: formData.authType === 'oauth2' && formData.oauthClientSecret ? 
              formData.oauthClientSecret : undefined,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showToast('MCP server added successfully', 'success');
        
        // If OAuth 2.1, initiate the authorization flow
        if (formData.authType === 'oauth2' && data.server?.id) {
          try {
            const oauth2Response = await fetch(`/api/agents/${agentId}/mcp-servers/${data.server.id}/oauth2/initiate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                source,
              }),
            });
            
            
            if (oauth2Response.ok) {
              const oauthData = await oauth2Response.json();
              
              if (oauthData.authorizationUrl) {
                // Redirect to OAuth 2.1 authorization
                window.location.href = oauthData.authorizationUrl;
                return; // Don't close modal or call onServerAdded since we're redirecting
              } else {
                showToast('No authorization URL returned', 'error');
              }
            } else {
              const errorData = await oauth2Response.text();
              showToast('Failed to initiate OAuth 2.1 authorization', 'error');
            }
          } catch (error) {
            showToast('Failed to initiate OAuth2 authorization', 'error');
          }
        } else {
          // Only call onServerAdded if we're not doing OAuth
          onServerAdded();
        }
      } else {
        const error = await response.json();
        showToast(error.error || 'Failed to add MCP server', 'error');
      }
    } catch (error) {
      console.error('Failed to add MCP server:', error);
      showToast('Failed to add MCP server', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Add MCP Server</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Server Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="My MCP Server"
              required
            />
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700">
              Server URL <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="https://mcp-server.example.com"
                required
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              The HTTPS endpoint URL for your MCP server (HTTP not supported)
            </p>
          </div>

          <div>
            <label htmlFor="authType" className="block text-sm font-medium text-gray-700">
              Authentication Type
            </label>
            <select
              id="authType"
              value={formData.authType}
              onChange={(e) => setFormData({ ...formData, authType: e.target.value as 'none' | 'bearer' | 'custom' | 'oauth2' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="none">No Authentication</option>
              <option value="bearer">Bearer Token</option>
              <option value="custom">Custom Headers</option>
              <option value="oauth2">OAuth 2.1</option>
            </select>
          </div>

          {formData.authType === 'bearer' && (
            <div>
              <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
                Bearer Token <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="apiKey"
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Your bearer token"
                required={formData.authType === 'bearer'}
              />
              <p className="mt-1 text-sm text-gray-500">
                Token will be sent as: Authorization: Bearer &lt;token&gt;
              </p>
            </div>
          )}

          {formData.authType === 'custom' && (
            <div>
              <label htmlFor="customHeaders" className="block text-sm font-medium text-gray-700">
                Custom Headers (JSON) <span className="text-red-500">*</span>
              </label>
              <textarea
                id="customHeaders"
                value={formData.customHeaders}
                onChange={(e) => setFormData({ ...formData, customHeaders: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder='{"X-API-Key": "your-key", "X-Custom-Header": "value"}'
                rows={3}
                required={formData.authType === 'custom'}
              />
              <p className="mt-1 text-sm text-gray-500">
                Provide headers as a JSON object
              </p>
            </div>
          )}

          {formData.authType === 'oauth2' && (
            <>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      OAuth 2.1 authentication will be configured after adding the server. 
                      You'll be redirected to complete the authorization flow.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="oauthClientId" className="block text-sm font-medium text-gray-700">
                  OAuth Client ID (optional)
                </label>
                <input
                  type="text"
                  id="oauthClientId"
                  value={formData.oauthClientId}
                  onChange={(e) => setFormData({ ...formData, oauthClientId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Your OAuth app's client ID"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Required for services that don't support dynamic client registration (e.g., GitHub)
                </p>
              </div>

              <div>
                <label htmlFor="oauthClientSecret" className="block text-sm font-medium text-gray-700">
                  OAuth Client Secret (optional)
                </label>
                <input
                  type="password"
                  id="oauthClientSecret"
                  value={formData.oauthClientSecret}
                  onChange={(e) => setFormData({ ...formData, oauthClientSecret: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Your OAuth app's client secret"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Some OAuth apps require a client secret for the token exchange
                </p>
              </div>
            </>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  The MCP server must support HTTP transport and expose tools via the standard MCP protocol.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Adding...' : 'Add Server'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}